/**
 * @workspace/network
 *
 * Shared HTTP client with error handling, retries, timeouts, and observability.
 *
 * @example Basic usage
 * ```ts
 * import { createClient } from '@workspace/network';
 *
 * const api = createClient({
 *   baseUrl: 'https://api.example.com',
 *   timeout: 10000,
 *   retry: { attempts: 3 },
 * });
 *
 * // GET request
 * const { data } = await api.get<User>('/users/123');
 *
 * // POST request
 * const { data: created } = await api.post<User>('/users', { name: 'John' });
 *
 * // Non-throwing variant
 * const result = await api.safeRequest<User>('/users/123');
 * if (result.ok) {
 *   console.log(result.data);
 * } else {
 *   console.error(result.error.code);
 * }
 * ```
 *
 * @example With observability
 * ```ts
 * import { createClient } from '@workspace/network';
 * import { tracedFetch } from '@workspace/observability';
 *
 * const api = createClient({
 *   baseUrl: 'https://api.example.com',
 *   fetch: tracedFetch, // All requests will be traced
 * });
 * ```
 *
 * @example Error handling
 * ```ts
 * import { createClient, NetworkError } from '@workspace/network';
 *
 * const api = createClient({ baseUrl: 'https://api.example.com' });
 *
 * try {
 *   await api.get('/protected');
 * } catch (error) {
 *   if (NetworkError.isStatus(error, 401)) {
 *     // Handle unauthorized
 *   } else if (NetworkError.isServerError(error)) {
 *     // Handle 5xx errors
 *   }
 * }
 * ```
 */

// Core client
export { createClient } from './client.js';

// Errors
export { NetworkError } from './errors.js';

// Identity forwarding (S2S user attribution — see @workspace/backend-utils
// readUnverifiedIdentity for the receiver side)
export {
  forwardedIdentityHeaders,
  extractInboundToken,
  FORWARDED_IDENTITY_HEADER,
  ATTRIBUTION_IDENTITY_HEADER,
  serviceCallHeaders,
  tokenExpiresWithin,
  type HeaderSource,
} from './identity-forward.js';

// Types
export type {
  ClientConfig,
  ErrorCode,
  HttpMethod,
  NetworkClient,
  NetworkError as INetworkError,
  NetworkResponse,
  RequestOptions,
  Result,
  RetryConfig,
} from './types.js';

// Auth utilities
export {
  createAuthRefresh,
  refreshAuth,
  createAuthFetch,
  withRetryFetch,
  type CreateAuthRefreshOptions,
  type CreateAuthFetchOptions,
  type RetryFetchOptions,
} from './auth.js';
export {
  installAuthRefresh,
  type AuthRefreshHandle,
  type InstallAuthRefreshOptions,
} from './auth-refresh-browser.js';

// Observability integration
export {
  createTracingFetch,
  withObservability,
  type ObservabilityHooks,
  type InfraVizOptions,
} from './observability.js';
